/**
 * useFeatureTheme hook - Provides access to all feature-relevant theme tokens
 * Used by feature pages to consume semantic design tokens in a consistent way
 * 
 * Usage:
 * const { semantic, chat, spacing, radius, shadows, colors } = useFeatureTheme();
 */

import { useTheme } from '@mui/material/styles';
import {
  semantic,
  status,
  attachMenu,
  chat,
} from '../tokens/semantic';
import { spacing, controlHeights, density } from '../tokens/spacing';
import { radius, radiusPx } from '../tokens/radius';
import { shadows } from '../tokens/shadows';
import { colors } from '../tokens/colors';
import { typography, typographyStyles } from '../tokens/typography';

/**
 * Hook to access theme tokens for feature components
 * Returns all semantic tokens, spacing, radius, shadows, and colors
 * 
 * @returns {Object} Theme tokens object
 */
export const useFeatureTheme = () => {
  const muiTheme = useTheme();

  const customTokens = muiTheme?.customTokens || {};
  const resolvedSemantic = customTokens.semantic || semantic;

  return {
    semantic: resolvedSemantic,
    chat: resolvedSemantic.chat || chat,
    status: resolvedSemantic.status || status,
    attachMenu: resolvedSemantic.attachMenu || attachMenu,
    spacing: customTokens.spacing || spacing,
    controlHeights: customTokens.controlHeights || controlHeights,
    density: customTokens.density || density,
    radius: customTokens.radius || radius,
    radiusPx: customTokens.radiusPx || radiusPx,
    shadows: customTokens.shadows || shadows,
    typography: customTokens.typography || typography,
    typographyStyles: customTokens.typographyStyles || typographyStyles,
    colors,
    muiTheme,
  };
};

export default useFeatureTheme;
